/**
 * @component EmptyState
 * @description Generischer Empty-State f\u00fcr leere Sektionen. Liefert ein konsistentes
 *              Design ohne riesige weiSSe Bl\u00f6cke. Optional CTA-Button.
 *
 * @prop {ReactNode}  [icon]         - Lucide-Icon oder eigenes SVG/Element (linksb\u00fcndig kreisrund hinterlegt)
 * @prop {string}     title          - Headline
 * @prop {string}     [description]  - Erkl\u00e4rungs-Sub-Text
 * @prop {string}     [ctaLabel]     - Optionaler CTA-Button-Text
 * @prop {Function}   [ctaAction]    - Click-Handler f\u00fcr CTA (wenn href fehlt)
 * @prop {string}     [ctaHref]      - Optional: rendert <a> statt <button>
 * @prop {string}     [variant]      - 'default' (gross) | 'compact' (kleiner f\u00fcr Inline-Use)
 * @prop {string}     [className]    - Optionale Zusatzklassen
 */
export default function EmptyState({
  icon,
  title,
  description,
  ctaLabel,
  ctaAction,
  ctaHref,
  variant = 'default',
  className = '',
}) {
  const compact = variant === 'compact';
  const padY = compact ? 'py-6' : 'py-10';
  const iconSize = compact ? 'w-10 h-10' : 'w-14 h-14';
  const titleSize = compact ? 'text-sm' : 'text-base';

  const cta = ctaLabel ? (
    ctaHref ? (
      <a
        href={ctaHref}
        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] transition-colors"
      >
        {ctaLabel}
        <span aria-hidden>&rarr;</span>
      </a>
    ) : (
      <button
        type="button"
        onClick={ctaAction}
        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] transition-colors"
      >
        {ctaLabel}
        <span aria-hidden>&rarr;</span>
      </button>
    )
  ) : null;

  return (
    <div
      className={`flex flex-col items-center justify-center text-center px-5 ${padY} ${className}`}
    >
      {icon && (
        <div
          className={`${iconSize} rounded-full flex items-center justify-center mb-3`}
          style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-tertiary, #6B7280)' }}
        >
          {icon}
        </div>
      )}
      <div className={`${titleSize} font-medium`} style={{ color: 'var(--text-primary, #1A1A2E)' }}>
        {title}
      </div>
      {description && (
        <div
          className="text-xs mt-1 max-w-md"
          style={{ color: 'var(--text-tertiary, #6B7280)' }}
        >
          {description}
        </div>
      )}
      {cta}
    </div>
  );
}
